import { css } from '@emotion/css';
import { usageTracker, UsageTrackerEvent } from '@volumental/analytic';
import arrowIcon from '@volumental/assets/icons/backArrow.black.svg';
import introImg from '@volumental/assets/images/size-chart/scan-thumbnail.gif';
import { LoaderDots, uiConfig } from '@volumental/common';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStore, useViewStore } from '../../../store';
import {
  AppMode,
  type RecommendationError,
  type SizeRecommendation,
} from '../../../types/types';
import { formatSizeLocale, getStartView } from './scanner-controller-helper';

const imgStyle = css`
  max-width: 56px;
  max-height: 56px;
  flex-shrink: 0;
  border-radius: 8px;
`;
const buttonStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: left;
  width: 100%;
  background: white;
  border-radius: 1rem;
  border-color: ${uiConfig.styles.colors.black};
  border-width: 0.07rem;
  border-style: solid;
  padding: 12px;
`;
const textStyle = css`
  color: #000;
  font-family: ${uiConfig.styles.fontFamily};
  text-align: left;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  flex-direction: column;
  margin: 0px 16px;
`;
const rowStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;
const arrowStyle = css`
  position: relative;
  height: 1.7rem;
  width: 1.7rem;
  transform: rotate(180deg);
  margin: 0;
  padding: 0;
`;
const imageTextStyle = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;
const loadingStyle = css`
  padding: 0 1rem;
`;

interface ButtonContentProps {
  recommendedSize?: SizeRecommendation;
  showWidth?: boolean;
  error?: RecommendationError;
  loading: boolean;
}

function ButtonContent({
  recommendedSize,
  showWidth,
  error,
  loading,
}: ButtonContentProps): JSX.Element {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className={loadingStyle}>
        <LoaderDots />
      </div>
    );
  }
  if (error?.code === 'SIZE_NOT_AVAILABLE') {
    return (
      <div className={textStyle}>
        {t('scannerButton.notAvailableInYourSize')}
      </div>
    );
  }
  if (recommendedSize) {
    return (
      <div className={textStyle}>
        <span>{t('scannerButton.yourSize')}</span>
        <span style={{ fontSize: '21px', fontWeight: 'bold' }}>
          {formatSizeLocale(recommendedSize.sizeLocale)}{' '}
          {recommendedSize.lengthSize}{' '}
          {showWidth && recommendedSize.widthSize
            ? `(${recommendedSize.widthSize.replace('1', '')})`
            : null}
        </span>
      </div>
    );
  }
  return (
    <div className={textStyle}>
      <span>{t('scannerButton.volumentalSizing')}</span>
      <span style={{ fontSize: '21px', fontWeight: 'bold' }}>
        {t('scannerButton.findYourSize')}
      </span>
    </div>
  );
}

export function ScannerBorderedButton({
  recommendedSize,
  showWidth = true,
  error,
  loading,
}: ButtonContentProps): JSX.Element {
  const { setMode } = useSessionStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const { t } = useTranslation();
  const goToScannerFlow = (): void => {
    usageTracker.track(UsageTrackerEvent.GoToScannerFromSizeChart);
    setMode(AppMode.Recommendation);
    setCurrentView(
      getStartView(
        recommendedSize !== undefined || error?.code === 'SIZE_NOT_AVAILABLE',
      ),
    );
  };

  return (
    <button className={buttonStyle} onClick={goToScannerFlow}>
      <div className={rowStyle}>
        <div className={imageTextStyle}>
          <img
            src={introImg}
            role="presentation"
            alt={t('imageAlt.scannerBt')}
            className={imgStyle}
          />
          <ButtonContent
            recommendedSize={recommendedSize}
            showWidth={showWidth}
            error={error}
            loading={loading}
          />
        </div>
        <img
          className={arrowStyle}
          src={arrowIcon}
          alt={t('imageAlt.arrowIcon')}
        />
      </div>
    </button>
  );
}
